import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { markets, REGION_META } from "../data/markets";
import RegionMap from "../components/RegionMap";
import { PageHead } from "../components/PageHead";
import { Reveal } from "../components/Reveal";
import { useTitle } from "../lib/useTitle";

const CHIPS = [
  { id: "all", label: "All regions" },
  { id: "in", label: "India" },
  { id: "me", label: "Middle East" },
  { id: "af", label: "Africa" },
];

const slug = (t) => t.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "");

export default function WhereWeWork() {
  useTitle("Where We Work | Amplified Minds");
  const [params, setParams] = useSearchParams();
  const marketParam = params.get("market");
  const [region, setRegion] = useState("all");
  const [highlight, setHighlight] = useState(null);

  const isoFor = (name) => markets.find((m) => m.name === name)?.iso;

  useEffect(() => {
    if (!marketParam) {
      setHighlight(null);
      return undefined;
    }
    const iso = isoFor(marketParam);
    setHighlight(iso || null);
    const timer = window.setTimeout(() => {
      document.getElementById(`country-${slug(marketParam)}`)?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 300);
    const clear = window.setTimeout(() => setHighlight(null), 3000);
    return () => {
      window.clearTimeout(timer);
      window.clearTimeout(clear);
    };
  }, [marketParam]);

  const fadedIsos = useMemo(() => {
    if (highlight) return new Set(markets.filter((m) => m.iso !== highlight).map((m) => m.iso));
    if (region === "all") return null;
    return new Set(markets.filter((m) => m.region !== region).map((m) => m.iso));
  }, [region, highlight]);

  const pickRegion = (id) => {
    setRegion(id);
    if (marketParam) setParams({}, { replace: true });
  };

  return (
    <>
      <PageHead
        crumbs={[{ label: "Home", to: "/" }, { label: "Where we work" }]}
        title="Where we work"
        lede="We support clients across India, the Middle East and Africa. Choose a region, or select a country on the map."
      >
        <div className="mt-7 flex flex-wrap gap-2" role="tablist" aria-label="Filter by region">
          {CHIPS.map((c) => (
            <button
              key={c.id}
              type="button"
              role="tab"
              aria-selected={region === c.id}
              data-testid={`region-chip-${c.id}`}
              onClick={() => pickRegion(c.id)}
              className="flex items-center gap-2 rounded-full border px-5 py-2 text-sm font-medium transition-colors duration-200"
              style={
                region === c.id
                  ? { background: "var(--ink)", borderColor: "var(--ink)", color: "var(--on-accent)" }
                  : { background: "var(--surface)", borderColor: "var(--line)", color: "var(--muted)" }
              }
            >
              {c.id !== "all" && (
                <span className="inline-block h-2 w-2 rounded-full" style={{ background: REGION_META[c.id].color }} />
              )}
              {c.label}
            </button>
          ))}
        </div>
      </PageHead>

      <section className="py-16 md:py-24">
        <div className="mx-auto flex w-full max-w-[1200px] flex-col gap-10 px-4 sm:px-6 min-[960px]:grid min-[960px]:grid-cols-2 lg:px-8">
          <Reveal>
            <div className="rounded-[22px] border border-line bg-surface p-4 shadow-surface sm:p-6 min-[960px]:sticky min-[960px]:top-24" data-testid="www-map-panel">
              <RegionMap idPrefix="www" fadedIsos={fadedIsos} highlightIso={highlight} />
            </div>
          </Reveal>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5" data-testid="country-grid">
            {markets.map((m, i) => {
              const meta = REGION_META[m.region];
              const isHot = highlight === m.iso;
              return (
                <Reveal key={m.iso} delay={(i % 2) * 0.07}>
                  <article
                    id={`country-${slug(m.name)}`}
                    data-testid={`country-card-${slug(m.name)}`}
                    className="flex h-full scroll-mt-28 flex-col rounded-[14px] border border-line border-l-4 bg-surface p-5 shadow-surface transition-shadow duration-300"
                    style={{
                      borderLeftColor: meta.color,
                      boxShadow: isHot ? `0 0 0 3px ${meta.soft}` : undefined,
                    }}
                  >
                    <p className="font-mono text-[10px] font-medium uppercase tracking-[0.14em]" style={{ color: meta.color }}>
                      {m.subRegion}
                    </p>
                    <h3 className="mt-2 font-display text-lg font-bold text-ink">{m.name}</h3>
                    <p className="mt-1.5 flex-1 text-sm text-mute">{m.cities.map((c) => c.name).join(" · ")}</p>
                    <Link
                      to={`/contact?market=${encodeURIComponent(m.name)}`}
                      data-testid={`country-discuss-${slug(m.name)}`}
                      className="mt-4 self-start text-sm font-semibold transition-colors duration-200 hover:text-ink"
                      style={{ color: meta.color }}
                    >
                      Discuss {m.name} →
                    </Link>
                  </article>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
}
